/* oxlint-disable jsx-a11y/prefer-tag-over-role */
import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import {
  faCheckToSlot as faCheckToSlotRegular,
  faCompress as faCompressRegular,
  faExpand as faExpandRegular,
  faPause as faPauseRegular,
  faPlay as faPlayRegular,
  faScissors as faScissorsRegular,
  faStepBackward as faStepBackwardRegular,
  faStepForward as faStepForwardRegular,
  faVolume as faVolumeRegular,
  faVolumeSlash as faVolumeSlashRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faBackwardStep,
  faCompress,
  faExpand,
  faForwardStep,
  faPause,
  faPlay,
  faScissors,
  faVolume,
  faVolumeSlash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import { useVideoPlayerTaskNavigation } from './ui.video-player-controls.hooks';
import TimelineHighlight from './ui.video-player-timeline-highlight';
import TimelineTrimRange from './ui.video-player-timeline-trim-range';
import { CONTROL_BUTTON_CLASSES } from './ui.video-player.consts';
import { formatTime } from './ui.video-player.helpers';
import { usePointerScrub } from './ui.video-player.hooks';
import {
  Breakpoint,
  TrimRange,
  VideoPlayerState,
} from './ui.video-player.types';

interface Props {
  timelineRef: React.RefObject<HTMLDivElement | null>;
  timelineHeight: number;
  handleTimelineClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  seekTo: (time: number) => void;
  state: VideoPlayerState;
  renderBreakpoints: React.ReactNode;
  togglePlayPause: () => void;
  rewind10s: () => void;
  forward10s: () => void;
  toggleMute: () => void;
  toggleFullscreen: () => void;
  isFullscreen: boolean;
  selectedBreakpointId?: string | null;
  breakpoints: Breakpoint[];
  trimRange?: TrimRange | null;
  onTrimRangeChange?: (range: TrimRange) => void;
  onClipStart?: (currentTime: number) => void;
  onScrubStart: () => void;
  onScrubEnd: () => void;
  showTaskNavigation?: boolean;
  disabled?: boolean;
}

const Timeline = styled.div<{ height: number }>`
  height: ${(props) => props.height}px;
`;

const ProgressBar = styled.div<{ width: string }>`
  width: ${(props) => props.width};
  transform: translateZ(0);
`;

const ProgressThumb = styled.div<{ height: number; left: string }>`
  height: ${(props) => props.height}px;
  left: ${(props) => props.left};
  transform: translate(-50%, -50%);
`;

const SegmentFill = styled.div<{ widthPercent: number }>`
  width: ${(props) => props.widthPercent}%;
`;

const TimelineSegment = styled.div<{
  leftPercent: number;
  widthPercent: number;
}>`
  left: ${(props) => props.leftPercent}%;
  width: ${(props) => props.widthPercent}%;
`;

const TASK_MODE_TIMELINE_HEIGHT = 4;
const PILL_BUTTON_CLASSES =
  'flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-800 text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40';
const PILL_TEXT_CLASSES =
  'flex h-10 items-center gap-2 rounded-full bg-gray-800 px-4 text-sm font-bold text-gray-100';

const VideoPlayerControls = ({
  timelineRef,
  timelineHeight,
  handleTimelineClick,
  seekTo,
  state,
  renderBreakpoints,
  togglePlayPause,
  rewind10s,
  forward10s,
  toggleMute,
  toggleFullscreen,
  isFullscreen,
  selectedBreakpointId,
  breakpoints,
  trimRange,
  onTrimRangeChange,
  onClipStart,
  onScrubStart,
  onScrubEnd,
  showTaskNavigation = false,
  disabled = false,
}: Props) => {
  const t = useTranslations('VideoPlayer.controls');
  const tVideoPlayer = useTranslations('VideoPlayer');

  const isTrimMode = !!trimRange && !!onTrimRangeChange;

  const [pendingScrubReleaseTime, setPendingScrubReleaseTime] = useState<
    number | null
  >(null);

  const handleScrubSeek = (time: number) => {
    setPendingScrubReleaseTime(time);
    seekTo(time);
  };

  const {
    onPointerDown: onScrubPointerDown,
    onPointerMove: onScrubPointerMove,
    onPointerUp: onScrubPointerUp,
    onPointerCancel: onScrubPointerCancel,
    isScrubbing,
    scrubTime,
    wasDragRef,
  } = usePointerScrub({
    timelineRef,
    duration: state.duration,
    trimRange,
    disabled: isTrimMode,
    onSeekCommit: handleScrubSeek,
    onScrubStart,
    onScrubEnd,
  });

  useEffect(() => {
    if (pendingScrubReleaseTime === null) return;
    const timer = setTimeout(() => setPendingScrubReleaseTime(null), 800);
    return () => clearTimeout(timer);
  }, [pendingScrubReleaseTime]);

  const displayTime = isScrubbing
    ? scrubTime
    : (pendingScrubReleaseTime ?? state.currentTime);
  const isShowingScrubPreview = isScrubbing || pendingScrubReleaseTime !== null;
  const progressPercent = state.duration
    ? `${Math.min((displayTime / state.duration) * 100, 100)}%`
    : '0%';

  const handleTimelineClickGuarded = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (wasDragRef.current) return;
    setPendingScrubReleaseTime(null);
    handleTimelineClick(event);
  };

  const {
    showTaskNav,
    isPrevTaskDisabled,
    isNextTaskDisabled,
    taskNumber,
    handlePrevTask,
    handleNextTask,
    taskSegments,
  } = useVideoPlayerTaskNavigation({
    showTaskNavigation,
    breakpoints,
    currentTime: state.currentTime,
    displayTime,
    duration: state.duration,
    seekTo,
  });

  const lastVisibleSegmentIndex = taskSegments.reduce(
    (acc, seg, i) => (seg.widthPercent > 0 ? i : acc),
    -1
  );

  if (showTaskNav) {
    return (
      <div
        data-test='video-player-controls'
        data-disabled={disabled}
        inert={disabled}
        className='flex shrink-0 flex-col gap-4 rounded-b-2xl p-2 select-none data-[disabled=true]:opacity-40'
      >
        <Timeline
          data-test='video-timeline'
          data-scrubbing={isScrubbing}
          ref={timelineRef}
          className='group relative w-full cursor-pointer touch-none select-none'
          height={TASK_MODE_TIMELINE_HEIGHT}
          draggable={false}
          onClick={handleTimelineClickGuarded}
          onPointerDown={onScrubPointerDown}
          onPointerMove={onScrubPointerMove}
          onPointerUp={onScrubPointerUp}
          onPointerCancel={onScrubPointerCancel}
          role='slider'
          aria-label='Video timeline'
          aria-valuemin={0}
          aria-valuemax={state.duration}
          aria-valuenow={displayTime}
          tabIndex={0}
        >
          {taskSegments.map((seg, i) => (
            <TimelineSegment
              key={seg.id}
              aria-hidden='true'
              leftPercent={seg.leftPercent}
              widthPercent={seg.widthPercent}
              className={cn(
                'absolute inset-y-0 overflow-hidden bg-gray-700',
                i === 0 && 'rounded-l',
                i === lastVisibleSegmentIndex && 'rounded-r'
              )}
            >
              <SegmentFill
                data-scrubbing={isShowingScrubPreview}
                className='absolute inset-y-0 left-0 bg-rose-600 transition-[width] duration-100 ease-out data-[scrubbing=true]:transition-none'
                widthPercent={seg.fillRatio * 100}
              />
              {i < lastVisibleSegmentIndex ? (
                <div className='absolute inset-y-0 right-0 z-10 w-0.5 bg-gray-900' />
              ) : null}
            </TimelineSegment>
          ))}
          <ProgressThumb
            data-scrubbing={isShowingScrubPreview}
            aria-hidden='true'
            className='pointer-events-none absolute top-1/2 z-20 size-3 rounded-full bg-rose-600 shadow-md transition-[left] duration-100 ease-out data-[scrubbing=true]:transition-none'
            height={12}
            left={progressPercent}
          />
        </Timeline>

        <div className='flex w-full items-center justify-between'>
          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-1'>
              <button
                data-test='play-pause'
                onClick={togglePlayPause}
                className={PILL_BUTTON_CLASSES}
                aria-label={state.isPlaying ? 'Pause video' : 'Play video'}
              >
                <FontAwesomeIcon
                  icon={state.isPlaying ? faPauseRegular : faPlayRegular}
                  fixedWidth
                  className='text-base'
                  aria-hidden='true'
                />
              </button>
              <div data-test='time-display' className={PILL_TEXT_CLASSES}>
                <span>{formatTime(displayTime)}</span>
                <span>/</span>
                <span>{formatTime(state.duration)}</span>
              </div>
            </div>

            <div
              data-test='task-navigation'
              className='flex items-center gap-1'
            >
              <button
                data-test='prev-task'
                onClick={handlePrevTask}
                disabled={isPrevTaskDisabled}
                className={PILL_BUTTON_CLASSES}
                aria-label='Previous task'
              >
                <FontAwesomeIcon
                  icon={faStepBackwardRegular}
                  fixedWidth
                  className='text-base'
                  aria-hidden='true'
                />
              </button>

              <div data-test='task-pill' className={PILL_TEXT_CLASSES}>
                <FontAwesomeIcon
                  icon={faCheckToSlotRegular}
                  fixedWidth
                  className='text-base'
                  aria-hidden='true'
                />
                <span>Task {taskNumber}</span>
              </div>

              <button
                data-test='next-task'
                onClick={handleNextTask}
                disabled={isNextTaskDisabled}
                className={PILL_BUTTON_CLASSES}
                aria-label='Next task'
              >
                <FontAwesomeIcon
                  icon={faStepForwardRegular}
                  fixedWidth
                  className='text-base'
                  aria-hidden='true'
                />
              </button>
            </div>
          </div>

          <div className='flex items-center gap-1'>
            {onClipStart ? (
              <button
                data-test='clip-start'
                onClick={() => onClipStart(state.currentTime)}
                className={PILL_BUTTON_CLASSES}
                aria-label='Start clip'
              >
                <FontAwesomeIcon
                  icon={faScissorsRegular}
                  fixedWidth
                  className='text-base'
                  aria-hidden='true'
                />
              </button>
            ) : null}
            <button
              data-test='mute'
              onClick={toggleMute}
              className={PILL_BUTTON_CLASSES}
              aria-label={state.isMuted ? 'Unmute video' : 'Mute video'}
            >
              <FontAwesomeIcon
                icon={state.isMuted ? faVolumeSlashRegular : faVolumeRegular}
                fixedWidth
                className='text-base'
                aria-hidden='true'
              />
            </button>
            <button
              data-test='fullscreen'
              onClick={toggleFullscreen}
              className={PILL_BUTTON_CLASSES}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              <FontAwesomeIcon
                icon={isFullscreen ? faCompressRegular : faExpandRegular}
                fixedWidth
                className='text-base'
                aria-hidden='true'
              />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-test='video-player-controls'
      data-disabled={disabled}
      inert={disabled}
      className='h-26.25 shrink-0 select-none data-[disabled=true]:opacity-40'
    >
      <div className='space-y-4 px-6 pt-5 pb-4'>
        {/* Timeline */}
        <div className='space-y-2'>
          <Timeline
            data-test='video-timeline'
            data-scrubbing={isScrubbing}
            ref={timelineRef}
            className='group relative cursor-pointer touch-none select-none'
            height={timelineHeight}
            draggable={false}
            onClick={handleTimelineClickGuarded}
            onPointerDown={onScrubPointerDown}
            onPointerMove={onScrubPointerMove}
            onPointerUp={onScrubPointerUp}
            onPointerCancel={onScrubPointerCancel}
            role='slider'
            aria-label={t('timelineAria')}
            aria-valuemin={0}
            aria-valuemax={state.duration}
            aria-valuenow={displayTime}
            tabIndex={0}
          >
            {/* Top Timeline Layer (16px) - Main progress and highlights */}
            <div className='absolute inset-0 rounded-md'>
              {/* Timeline Track - White with 16% opacity */}
              <div className='absolute inset-0 rounded-md bg-white/16 opacity-55' />

              <ProgressBar
                data-scrubbing={isShowingScrubPreview}
                className='absolute top-0 left-0 h-full rounded-md rounded-r-none bg-indigo-400 transition-all duration-100 ease-out will-change-transform data-[scrubbing=true]:transition-none'
                width={progressPercent}
              />

              {/* Progress Thumb */}
              <ProgressThumb
                data-scrubbing={isShowingScrubPreview}
                className='absolute top-1/2 z-20 w-px transform rounded-full bg-rose-500 shadow-lg transition-opacity duration-200 data-[scrubbing=true]:transition-none'
                height={timelineHeight + 10}
                left={progressPercent}
              />

              {isTrimMode ? (
                <TimelineTrimRange
                  trimRange={trimRange}
                  duration={state.duration}
                  timelineHeight={timelineHeight}
                  timelineRef={timelineRef}
                  onTrimRangeChange={onTrimRangeChange}
                />
              ) : (
                <TimelineHighlight
                  selectedBreakpointId={selectedBreakpointId || null}
                  breakpoints={breakpoints}
                  duration={state.duration}
                  timelineHeight={timelineHeight}
                />
              )}

              {/* Breakpoints */}
              {renderBreakpoints}
            </div>
          </Timeline>
        </div>

        {/* Control Buttons */}
        <div className='flex items-center justify-between text-white'>
          <div className='flex items-center space-x-2'>
            {/* Play/Pause Button */}
            <button
              data-test='play-pause'
              onClick={togglePlayPause}
              className={cn(CONTROL_BUTTON_CLASSES.base)}
              aria-label={
                state.isPlaying
                  ? tVideoPlayer('pauseAria')
                  : tVideoPlayer('playAria')
              }
            >
              <FontAwesomeIcon
                icon={state.isPlaying ? faPause : faPlay}
                className='size-4'
                aria-hidden='true'
              />
            </button>

            {/* Rewind 10s */}
            <button
              data-test='rewind'
              onClick={rewind10s}
              className={cn(CONTROL_BUTTON_CLASSES.base)}
              aria-label={t('rewindAria')}
            >
              <FontAwesomeIcon
                icon={faBackwardStep}
                className='size-4'
                aria-hidden='true'
              />
            </button>

            {/* Forward 10s */}
            <button
              data-test='forward'
              onClick={forward10s}
              className={cn(CONTROL_BUTTON_CLASSES.base)}
              aria-label={t('forwardAria')}
            >
              <FontAwesomeIcon
                icon={faForwardStep}
                className='size-4'
                aria-hidden='true'
              />
            </button>

            {/* Time Display */}
            <div className='ml-2 text-sm font-bold text-white'>
              <span>{formatTime(displayTime)}</span>
              <span className='mx-1'>/</span>
              <span>{formatTime(state.duration)}</span>
            </div>
          </div>

          {/* Right Side Controls */}
          <div className='flex items-center space-x-3'>
            {onClipStart ? (
              <button
                data-test='clip-start'
                onClick={() => onClipStart(state.currentTime)}
                className={cn(CONTROL_BUTTON_CLASSES.base)}
                aria-label={t('startClipAria')}
              >
                <FontAwesomeIcon
                  icon={faScissors}
                  className='size-4'
                  aria-hidden='true'
                />
              </button>
            ) : null}
            {/* Volume Control */}
            <button
              data-test='mute'
              onClick={toggleMute}
              className={cn(CONTROL_BUTTON_CLASSES.base)}
              aria-label={state.isMuted ? t('unmuteAria') : t('muteAria')}
            >
              <FontAwesomeIcon
                icon={state.isMuted ? faVolumeSlash : faVolume}
                className='size-4'
                aria-hidden='true'
              />
            </button>

            {/* Fullscreen Button */}
            <button
              data-test='fullscreen'
              onClick={toggleFullscreen}
              className={cn(CONTROL_BUTTON_CLASSES.base)}
              aria-label={
                isFullscreen
                  ? t('exitFullscreenAria')
                  : t('enterFullscreenAria')
              }
            >
              <FontAwesomeIcon
                icon={isFullscreen ? faCompress : faExpand}
                className='size-4'
                aria-hidden='true'
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerControls;
